import { IncomeAt18, SipFrequency, TrumpContributionFrequency } from "@ctypes";
import { i18n, Translations } from "@i18n";

const DEFAULT_ACCOUNT_BALANCE = 1_000;
const SIDECAR_ANNUAL_RATE = 0.1;
const ASSUMED_INCOME_AT_65 = 72_500;
const FEDERAL_STANDARD_DEDUCTION = 16_100;
const HOLD_YEARS_TO_65 = 47;
const STUDENT_DEFER_YEARS = 6;
const DRAWDOWN_YEARS = 20;
const TAX_BAR_MIN_WIDTH_PERCENT = 12;
const TAX_BAR_MAX_WIDTH_PERCENT = 40;
const TAX_BAR_FULL_WIDTH_AT = 18_000_000;

const EARNED_INCOME_AT_CONVERSION: Record<IncomeAt18, number> = {
  [IncomeAt18.WorkingFullTime]: 34_000,
  [IncomeAt18.FullTimeStudent]: 60_000,
};

const FEDERAL_TAX_BRACKETS_2026: { min: number; max: number; rate: number }[] = [
  { min: 0, max: 12_400, rate: 0.1 },
  { min: 12_400, max: 50_400, rate: 0.12 },
  { min: 50_400, max: 105_700, rate: 0.22 },
  { min: 105_700, max: 201_775, rate: 0.24 },
  { min: 201_775, max: 256_225, rate: 0.32 },
  { min: 256_225, max: 640_600, rate: 0.35 },
  { min: 640_600, max: 999_999_999, rate: 0.37 },
];

type StateBracketEntry = { min: number; max: number; rate: number };
type StateData = { std_deduction: number; brackets: StateBracketEntry[] };

const STATE_TAX_DATA: Record<string, StateData> = {
  Alabama: {
    std_deduction: 3000,
    brackets: [
      { min: 0, max: 500, rate: 0.02 },
      { min: 500, max: 3000, rate: 0.04 },
      { min: 3000, max: 999_999_999, rate: 0.05 },
    ],
  },
  Alaska: { std_deduction: 0, brackets: [{ min: 0, max: 999_999_999, rate: 0 }] },
  Arizona: { std_deduction: 14_600, brackets: [{ min: 0, max: 999_999_999, rate: 0.025 }] },
  Arkansas: {
    std_deduction: 2340,
    brackets: [
      { min: 0, max: 5100, rate: 0.02 },
      { min: 5100, max: 10_300, rate: 0.04 },
      { min: 10_300, max: 999_999_999, rate: 0.039 },
    ],
  },
  California: {
    std_deduction: 5363,
    brackets: [
      { min: 0, max: 10_756, rate: 0.01 },
      { min: 10_756, max: 25_499, rate: 0.02 },
      { min: 25_499, max: 40_245, rate: 0.04 },
      { min: 40_245, max: 55_866, rate: 0.06 },
      { min: 55_866, max: 70_606, rate: 0.08 },
      { min: 70_606, max: 360_659, rate: 0.093 },
      { min: 360_659, max: 432_787, rate: 0.103 },
      { min: 432_787, max: 721_314, rate: 0.113 },
      { min: 721_314, max: 999_999_999, rate: 0.133 },
    ],
  },
  Colorado: { std_deduction: 14_600, brackets: [{ min: 0, max: 999_999_999, rate: 0.044 }] },
  Connecticut: {
    std_deduction: 15_000,
    brackets: [
      { min: 0, max: 10_000, rate: 0.02 },
      { min: 10_000, max: 50_000, rate: 0.045 },
      { min: 50_000, max: 100_000, rate: 0.055 },
      { min: 100_000, max: 200_000, rate: 0.06 },
      { min: 200_000, max: 250_000, rate: 0.065 },
      { min: 250_000, max: 500_000, rate: 0.069 },
      { min: 500_000, max: 999_999_999, rate: 0.0699 },
    ],
  },
  Delaware: {
    std_deduction: 3250,
    brackets: [
      { min: 0, max: 2000, rate: 0 },
      { min: 2000, max: 5000, rate: 0.022 },
      { min: 5000, max: 10_000, rate: 0.039 },
      { min: 10_000, max: 20_000, rate: 0.048 },
      { min: 20_000, max: 25_000, rate: 0.052 },
      { min: 25_000, max: 60_000, rate: 0.055 },
      { min: 60_000, max: 999_999_999, rate: 0.066 },
    ],
  },
  "District of Columbia": {
    std_deduction: 0,
    brackets: [
      { min: 0, max: 10_000, rate: 0.04 },
      { min: 10_000, max: 40_000, rate: 0.06 },
      { min: 40_000, max: 60_000, rate: 0.065 },
      { min: 60_000, max: 350_000, rate: 0.085 },
      { min: 350_000, max: 1_000_000, rate: 0.0925 },
      { min: 1_000_000, max: 999_999_999, rate: 0.1075 },
    ],
  },
  Florida: { std_deduction: 0, brackets: [{ min: 0, max: 999_999_999, rate: 0 }] },
  Georgia: { std_deduction: 12_000, brackets: [{ min: 0, max: 999_999_999, rate: 0.0519 }] },
  Hawaii: {
    std_deduction: 2200,
    brackets: [
      { min: 0, max: 2400, rate: 0.014 },
      { min: 2400, max: 4800, rate: 0.032 },
      { min: 4800, max: 9600, rate: 0.055 },
      { min: 9600, max: 14_400, rate: 0.064 },
      { min: 14_400, max: 19_200, rate: 0.068 },
      { min: 19_200, max: 24_000, rate: 0.072 },
      { min: 24_000, max: 48_000, rate: 0.076 },
      { min: 48_000, max: 150_000, rate: 0.079 },
      { min: 150_000, max: 175_000, rate: 0.0825 },
      { min: 175_000, max: 200_000, rate: 0.09 },
      { min: 200_000, max: 999_999_999, rate: 0.11 },
    ],
  },
  Idaho: { std_deduction: 14_600, brackets: [{ min: 0, max: 999_999_999, rate: 0.053 }] },
  Illinois: { std_deduction: 2775, brackets: [{ min: 0, max: 999_999_999, rate: 0.0495 }] },
  Indiana: { std_deduction: 1000, brackets: [{ min: 0, max: 999_999_999, rate: 0.0295 }] },
  Iowa: { std_deduction: 2280, brackets: [{ min: 0, max: 999_999_999, rate: 0.038 }] },
  Kansas: {
    std_deduction: 3500,
    brackets: [
      { min: 0, max: 15_000, rate: 0.031 },
      { min: 15_000, max: 30_000, rate: 0.0525 },
      { min: 30_000, max: 999_999_999, rate: 0.0558 },
    ],
  },
  Kentucky: { std_deduction: 3160, brackets: [{ min: 0, max: 999_999_999, rate: 0.035 }] },
  Louisiana: {
    std_deduction: 4500,
    brackets: [
      { min: 0, max: 12_500, rate: 0.0185 },
      { min: 12_500, max: 50_000, rate: 0.035 },
      { min: 50_000, max: 999_999_999, rate: 0.0425 },
    ],
  },
  Maine: {
    std_deduction: 14_600,
    brackets: [
      { min: 0, max: 24_500, rate: 0.058 },
      { min: 24_500, max: 58_050, rate: 0.0675 },
      { min: 58_050, max: 999_999_999, rate: 0.0715 },
    ],
  },
  Maryland: {
    std_deduction: 2400,
    brackets: [
      { min: 0, max: 1000, rate: 0.02 },
      { min: 1000, max: 2000, rate: 0.03 },
      { min: 2000, max: 3000, rate: 0.04 },
      { min: 3000, max: 100_000, rate: 0.0475 },
      { min: 100_000, max: 125_000, rate: 0.05 },
      { min: 125_000, max: 150_000, rate: 0.0525 },
      { min: 150_000, max: 250_000, rate: 0.055 },
      { min: 250_000, max: 999_999_999, rate: 0.065 },
    ],
  },
  Massachusetts: {
    std_deduction: 0,
    brackets: [
      { min: 0, max: 1_000_000, rate: 0.05 },
      { min: 1_000_000, max: 999_999_999, rate: 0.09 },
    ],
  },
  Michigan: { std_deduction: 5600, brackets: [{ min: 0, max: 999_999_999, rate: 0.0425 }] },
  Minnesota: {
    std_deduction: 14_575,
    brackets: [
      { min: 0, max: 31_690, rate: 0.0535 },
      { min: 31_690, max: 104_090, rate: 0.068 },
      { min: 104_090, max: 193_240, rate: 0.0785 },
      { min: 193_240, max: 999_999_999, rate: 0.0985 },
    ],
  },
  Mississippi: {
    std_deduction: 6000,
    brackets: [
      { min: 0, max: 10_000, rate: 0 },
      { min: 10_000, max: 999_999_999, rate: 0.04 },
    ],
  },
  Missouri: {
    std_deduction: 12_950,
    brackets: [
      { min: 0, max: 1207, rate: 0 },
      { min: 1207, max: 2414, rate: 0.02 },
      { min: 2414, max: 3621, rate: 0.025 },
      { min: 3621, max: 4828, rate: 0.03 },
      { min: 4828, max: 6035, rate: 0.035 },
      { min: 6035, max: 7242, rate: 0.04 },
      { min: 7242, max: 8449, rate: 0.045 },
      { min: 8449, max: 999_999_999, rate: 0.047 },
    ],
  },
  Montana: {
    std_deduction: 14_600,
    brackets: [
      { min: 0, max: 20_500, rate: 0.047 },
      { min: 20_500, max: 999_999_999, rate: 0.0565 },
    ],
  },
  Nebraska: {
    std_deduction: 7900,
    brackets: [
      { min: 0, max: 3700, rate: 0.0246 },
      { min: 3700, max: 22_170, rate: 0.0351 },
      { min: 22_170, max: 35_730, rate: 0.0501 },
      { min: 35_730, max: 999_999_999, rate: 0.0464 },
    ],
  },
  Nevada: { std_deduction: 0, brackets: [{ min: 0, max: 999_999_999, rate: 0 }] },
  "New Hampshire": { std_deduction: 0, brackets: [{ min: 0, max: 999_999_999, rate: 0 }] },
  "New Jersey": {
    std_deduction: 1000,
    brackets: [
      { min: 0, max: 20_000, rate: 0.014 },
      { min: 20_000, max: 35_000, rate: 0.0175 },
      { min: 35_000, max: 40_000, rate: 0.035 },
      { min: 40_000, max: 75_000, rate: 0.0553 },
      { min: 75_000, max: 500_000, rate: 0.0637 },
      { min: 500_000, max: 999_999_999, rate: 0.1075 },
    ],
  },
  "New Mexico": {
    std_deduction: 14_600,
    brackets: [
      { min: 0, max: 5500, rate: 0.017 },
      { min: 5500, max: 11_000, rate: 0.032 },
      { min: 11_000, max: 16_000, rate: 0.047 },
      { min: 16_000, max: 210_000, rate: 0.049 },
      { min: 210_000, max: 999_999_999, rate: 0.059 },
    ],
  },
  "New York": {
    std_deduction: 8000,
    brackets: [
      { min: 0, max: 17_150, rate: 0.04 },
      { min: 17_150, max: 23_600, rate: 0.045 },
      { min: 23_600, max: 27_900, rate: 0.0525 },
      { min: 27_900, max: 161_550, rate: 0.055 },
      { min: 161_550, max: 323_200, rate: 0.06 },
      { min: 323_200, max: 2_155_350, rate: 0.0685 },
      { min: 2_155_350, max: 25_000_000, rate: 0.0965 },
      { min: 25_000_000, max: 999_999_999, rate: 0.109 },
    ],
  },
  "North Carolina": {
    std_deduction: 12_750,
    brackets: [{ min: 0, max: 999_999_999, rate: 0.0399 }],
  },
  "North Dakota": {
    std_deduction: 14_600,
    brackets: [
      { min: 0, max: 44_725, rate: 0 },
      { min: 44_725, max: 225_975, rate: 0.019 },
      { min: 225_975, max: 999_999_999, rate: 0.025 },
    ],
  },
  Ohio: {
    std_deduction: 0,
    brackets: [
      { min: 0, max: 26_050, rate: 0 },
      { min: 26_050, max: 100_000, rate: 0.0275 },
      { min: 100_000, max: 999_999_999, rate: 0.035 },
    ],
  },
  Oklahoma: {
    std_deduction: 6350,
    brackets: [
      { min: 0, max: 1000, rate: 0.0025 },
      { min: 1000, max: 2500, rate: 0.0075 },
      { min: 2500, max: 3750, rate: 0.0175 },
      { min: 3750, max: 4900, rate: 0.0275 },
      { min: 4900, max: 7200, rate: 0.0375 },
      { min: 7200, max: 999_999_999, rate: 0.045 },
    ],
  },
  Oregon: {
    std_deduction: 2745,
    brackets: [
      { min: 0, max: 18_400, rate: 0.0475 },
      { min: 18_400, max: 46_200, rate: 0.0675 },
      { min: 46_200, max: 250_000, rate: 0.0875 },
      { min: 250_000, max: 999_999_999, rate: 0.099 },
    ],
  },
  Pennsylvania: { std_deduction: 0, brackets: [{ min: 0, max: 999_999_999, rate: 0.0307 }] },
  "Rhode Island": {
    std_deduction: 10_550,
    brackets: [
      { min: 0, max: 77_450, rate: 0.0375 },
      { min: 77_450, max: 176_050, rate: 0.0475 },
      { min: 176_050, max: 999_999_999, rate: 0.0599 },
    ],
  },
  "South Carolina": {
    std_deduction: 14_600,
    brackets: [
      { min: 0, max: 3460, rate: 0 },
      { min: 3460, max: 17_330, rate: 0.03 },
      { min: 17_330, max: 999_999_999, rate: 0.064 },
    ],
  },
  "South Dakota": { std_deduction: 0, brackets: [{ min: 0, max: 999_999_999, rate: 0 }] },
  Tennessee: { std_deduction: 0, brackets: [{ min: 0, max: 999_999_999, rate: 0 }] },
  Texas: { std_deduction: 0, brackets: [{ min: 0, max: 999_999_999, rate: 0 }] },
  Utah: { std_deduction: 886, brackets: [{ min: 0, max: 999_999_999, rate: 0.045 }] },
  Vermont: {
    std_deduction: 6500,
    brackets: [
      { min: 0, max: 45_400, rate: 0.0335 },
      { min: 45_400, max: 110_050, rate: 0.066 },
      { min: 110_050, max: 227_050, rate: 0.076 },
      { min: 227_050, max: 999_999_999, rate: 0.0875 },
    ],
  },
  Virginia: {
    std_deduction: 8500,
    brackets: [
      { min: 0, max: 3000, rate: 0.02 },
      { min: 3000, max: 5000, rate: 0.03 },
      { min: 5000, max: 17_000, rate: 0.05 },
      { min: 17_000, max: 999_999_999, rate: 0.0575 },
    ],
  },
  Washington: { std_deduction: 0, brackets: [{ min: 0, max: 999_999_999, rate: 0 }] },
  "West Virginia": {
    std_deduction: 0,
    brackets: [
      { min: 0, max: 10_000, rate: 0.0236 },
      { min: 10_000, max: 25_000, rate: 0.0315 },
      { min: 25_000, max: 40_000, rate: 0.0354 },
      { min: 40_000, max: 60_000, rate: 0.0472 },
      { min: 60_000, max: 999_999_999, rate: 0.0482 },
    ],
  },
  Wisconsin: {
    std_deduction: 13_150,
    brackets: [
      { min: 0, max: 14_320, rate: 0.035 },
      { min: 14_320, max: 28_640, rate: 0.044 },
      { min: 28_640, max: 315_310, rate: 0.053 },
      { min: 315_310, max: 999_999_999, rate: 0.0765 },
    ],
  },
  Wyoming: { std_deduction: 0, brackets: [{ min: 0, max: 999_999_999, rate: 0 }] },
};

export const US_STATE_ABBREVIATIONS = new Set([
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
]);

const STATE_ABBREV_TO_NAME: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

export type SidecarInputs = {
  currentAge: number;
  monthlyContribution: number;
  state: string;
  accountBalance: number;
  incomeAt18: IncomeAt18;
};

const calculateFederalTax = (taxableIncome: number): number => {
  if (taxableIncome <= 0) return 0;

  let tax = 0;
  for (const bracket of FEDERAL_TAX_BRACKETS_2026) {
    if (taxableIncome <= bracket.min) break;
    const taxableInBracket = Math.min(taxableIncome, bracket.max) - bracket.min;
    tax += taxableInBracket * bracket.rate;
  }
  return tax;
};

const resolveStateData = (stateInput: string): StateData | null => {
  const upper = stateInput.toUpperCase().trim();
  const fullName = STATE_ABBREV_TO_NAME[upper];
  if (fullName && STATE_TAX_DATA[fullName]) return STATE_TAX_DATA[fullName];

  const direct = STATE_TAX_DATA[stateInput];
  if (direct) return direct;

  return null;
};

const calculateStateTax = (taxableIncome: number, stateData: StateData | null): number => {
  if (!stateData || taxableIncome <= 0) return 0;

  let tax = 0;
  for (const bracket of stateData.brackets) {
    if (taxableIncome <= bracket.min) break;
    const taxableInBracket = Math.min(taxableIncome, bracket.max) - bracket.min;
    tax += taxableInBracket * bracket.rate;
  }
  return tax;
};

const projectToAge18 = (
  currentBalance: number,
  monthlyContribution: number,
  returnRate: number,
  currentAge: number,
): { balance: number; basis: number; gains: number } => {
  const n = 18 - currentAge;
  if (n <= 0) return { balance: currentBalance, basis: currentBalance, gains: 0 };

  const annualContrib = monthlyContribution * 12;
  let balance = currentBalance;

  for (let i = 0; i < n; i++) {
    balance = balance * (1 + returnRate) + annualContrib * Math.pow(1 + returnRate, 0.5);
  }

  const basis = annualContrib * n;
  return { balance, basis, gains: Math.max(0, balance - basis) };
};

const toMonthlyContribution = (
  amountInCents: number,
  frequency: TrumpContributionFrequency,
): number => {
  const amountInDollars = amountInCents / 100;
  switch (frequency) {
    case TrumpContributionFrequency.Weekly:
      return (amountInDollars * 52) / 12;
    case TrumpContributionFrequency.BiWeekly:
      return (amountInDollars * 26) / 12;
    case TrumpContributionFrequency.Monthly:
      return amountInDollars;
    case TrumpContributionFrequency.Annually:
      return amountInDollars / 12;
    default:
      return amountInDollars;
  }
};

export const getCurrentAgeFromDateOfBirth = (dateOfBirth?: string): number => {
  if (!dateOfBirth) {
    return 0;
  }

  const birthDate = new Date(dateOfBirth);

  if (Number.isNaN(birthDate.getTime())) {
    return 0;
  }

  const now = new Date();

  return Math.max(
    0,
    now.getFullYear() -
      birthDate.getFullYear() -
      (now < new Date(now.getFullYear(), birthDate.getMonth(), birthDate.getDate()) ? 1 : 0),
  );
};

export const calculateWithoutSidecar = ({
  currentAge,
  monthlyContribution,
  state,
  accountBalance,
  incomeAt18,
}: SidecarInputs) => {
  const stateData = resolveStateData(state);
  const stateStd = stateData?.std_deduction ?? 0;
  const isWorker = incomeAt18 === IncomeAt18.WorkingFullTime;

  const proj = projectToAge18(accountBalance, monthlyContribution, SIDECAR_ANNUAL_RATE, currentAge);

  const balanceAt65 = proj.balance * Math.pow(1 + SIDECAR_ANNUAL_RATE, HOLD_YEARS_TO_65);
  const taxableGainsAt65 = Math.max(0, balanceAt65 - proj.basis);

  // B1 — lump-sum withdrawal at 65
  const fedTaxableBase = Math.max(0, ASSUMED_INCOME_AT_65 - FEDERAL_STANDARD_DEDUCTION);
  const fedTaxableWithB1 = Math.max(
    0,
    ASSUMED_INCOME_AT_65 + taxableGainsAt65 - FEDERAL_STANDARD_DEDUCTION,
  );
  const fedTaxB1 = Math.max(
    0,
    calculateFederalTax(fedTaxableWithB1) - calculateFederalTax(fedTaxableBase),
  );

  const stateTaxableBase = Math.max(0, ASSUMED_INCOME_AT_65 - stateStd);
  const stateTaxableWithB1 = Math.max(0, ASSUMED_INCOME_AT_65 + taxableGainsAt65 - stateStd);
  const stateTaxB1 = Math.max(
    0,
    calculateStateTax(stateTaxableWithB1, stateData) -
      calculateStateTax(stateTaxableBase, stateData),
  );

  const totalTaxB1 = fedTaxB1 + stateTaxB1;

  // B2 — 20-year drawdown
  const annualTaxableSlice = taxableGainsAt65 / DRAWDOWN_YEARS;
  const fedTaxableWithB2 = Math.max(
    0,
    ASSUMED_INCOME_AT_65 + annualTaxableSlice - FEDERAL_STANDARD_DEDUCTION,
  );
  const annualFedTaxB2 = Math.max(
    0,
    calculateFederalTax(fedTaxableWithB2) - calculateFederalTax(fedTaxableBase),
  );

  const stateTaxableWithB2 = Math.max(0, ASSUMED_INCOME_AT_65 + annualTaxableSlice - stateStd);
  const annualStateTaxB2 = Math.max(
    0,
    calculateStateTax(stateTaxableWithB2, stateData) -
      calculateStateTax(stateTaxableBase, stateData),
  );

  const totalTaxB2 = (annualFedTaxB2 + annualStateTaxB2) * DRAWDOWN_YEARS;

  // Scenario A — conversion tax
  const conversionAge = isWorker ? 18 : 24;
  const earnedIncome = EARNED_INCOME_AT_CONVERSION[incomeAt18];
  const balanceAtConversion = isWorker
    ? proj.balance
    : proj.balance * Math.pow(1 + SIDECAR_ANNUAL_RATE, STUDENT_DEFER_YEARS);
  const gainsAtConversion = isWorker ? proj.gains : Math.max(0, balanceAtConversion - proj.basis);

  const fedTaxableNoConv = Math.max(0, earnedIncome - FEDERAL_STANDARD_DEDUCTION);
  const fedTaxableWithConv = Math.max(
    0,
    earnedIncome + gainsAtConversion - FEDERAL_STANDARD_DEDUCTION,
  );
  const fedTaxOnConversion = Math.max(
    0,
    calculateFederalTax(fedTaxableWithConv) - calculateFederalTax(fedTaxableNoConv),
  );

  const stateTaxableNoConv = Math.max(0, earnedIncome - stateStd);
  const stateTaxableWithConv = Math.max(0, earnedIncome + gainsAtConversion - stateStd);
  const stateTaxOnConversion = Math.max(
    0,
    calculateStateTax(stateTaxableWithConv, stateData) -
      calculateStateTax(stateTaxableNoConv, stateData),
  );

  const totalConversionTax = fedTaxOnConversion + stateTaxOnConversion;

  // Sidecar PMT — monthly savings needed to fund the conversion tax
  const monthsUntilConversion = Math.max(0, conversionAge - currentAge) * 12;
  let monthlySidecarSavings = 0;
  if (monthsUntilConversion > 0 && totalConversionTax > 0) {
    const monthlyRate = Math.pow(1 + SIDECAR_ANNUAL_RATE, 1 / 12) - 1;
    monthlySidecarSavings =
      (totalConversionTax * monthlyRate) / (Math.pow(1 + monthlyRate, monthsUntilConversion) - 1);
  }

  const taxBill = Math.round(totalTaxB1);
  const netSpendableB1 = Math.round(balanceAt65 - totalTaxB1);
  const netSpendableB2 = Math.round(balanceAt65 - totalTaxB2);

  return {
    taxBill,
    taxBillB2: Math.round(totalTaxB2),
    netSpendable: netSpendableB1,
    netSpendableB2,
    balanceAt65: Math.round(balanceAt65),
    conversionTax: Math.round(totalConversionTax),
    monthlySidecarSavings: Math.round(monthlySidecarSavings * 100) / 100,
  };
};

export const getTaxBarWidthPercent = (taxBill: number): number => {
  if (taxBill <= 0) {
    return TAX_BAR_MIN_WIDTH_PERCENT;
  }

  const ratio = taxBill / TAX_BAR_FULL_WIDTH_AT;
  const width =
    TAX_BAR_MIN_WIDTH_PERCENT + ratio * (TAX_BAR_MAX_WIDTH_PERCENT - TAX_BAR_MIN_WIDTH_PERCENT);

  return Math.min(TAX_BAR_MAX_WIDTH_PERCENT, Math.max(TAX_BAR_MIN_WIDTH_PERCENT, width));
};

export const calculateWithSidecar = ({
  currentAge,
  monthlyContribution,
  accountBalance,
  incomeAt18,
}: SidecarInputs) => {
  const isWorker = incomeAt18 === IncomeAt18.WorkingFullTime;

  const proj = projectToAge18(accountBalance, monthlyContribution, SIDECAR_ANNUAL_RATE, currentAge);

  const balanceAtConversion = isWorker
    ? proj.balance
    : proj.balance * Math.pow(1 + SIDECAR_ANNUAL_RATE, STUDENT_DEFER_YEARS);
  const taxFreeYears = isWorker ? HOLD_YEARS_TO_65 : HOLD_YEARS_TO_65 - STUDENT_DEFER_YEARS;
  const rothAt65 = balanceAtConversion * Math.pow(1 + SIDECAR_ANNUAL_RATE, taxFreeYears);

  const taxBill = 0;
  const netSpendable = Math.round(rothAt65);

  return { taxBill, netSpendable, balanceAt65: Math.round(rothAt65) };
};

export const getSidecarInputs = (
  dateOfBirth: string | undefined,
  trumpContributionAmountInCents: number | undefined,
  trumpContributionFrequency: TrumpContributionFrequency | undefined,
  userState: string | undefined,
  accountBalanceInCents?: number,
  incomeAt18?: IncomeAt18,
): SidecarInputs | null => {
  if (trumpContributionAmountInCents == null || !trumpContributionFrequency) {
    return null;
  }

  const currentAge = getCurrentAgeFromDateOfBirth(dateOfBirth);

  const monthlyContribution = toMonthlyContribution(
    trumpContributionAmountInCents,
    trumpContributionFrequency,
  );

  return {
    currentAge,
    monthlyContribution,
    state: userState || "NY",
    accountBalance: accountBalanceInCents ? accountBalanceInCents / 100 : DEFAULT_ACCOUNT_BALANCE,
    incomeAt18: incomeAt18 || IncomeAt18.WorkingFullTime,
  };
};

export const getIncomeAt18Label = (income: IncomeAt18): string => {
  switch (income) {
    case IncomeAt18.WorkingFullTime:
      return i18n.t(Translations.TrumpSidecarTaxCalculationRouteIncomeWorkingFullTime);
    case IncomeAt18.FullTimeStudent:
      return i18n.t(Translations.TrumpSidecarTaxCalculationRouteIncomeFullTimeStudent);
    default:
      return i18n.t(Translations.TrumpSidecarTaxCalculationRouteIncomeWorkingFullTime);
  }
};

export const fromMonthlySidecarContribution = (
  monthlySavings: number,
  frequency: SipFrequency,
): number => {
  switch (frequency) {
    case SipFrequency.Weekly:
      return (monthlySavings * 12) / 52;
    case SipFrequency.Monthly:
      return monthlySavings;
    default:
      return monthlySavings;
  }
};

export const getIncomeAt18Amount = (income: IncomeAt18): number => {
  return (
    EARNED_INCOME_AT_CONVERSION[income] ?? EARNED_INCOME_AT_CONVERSION[IncomeAt18.WorkingFullTime]
  );
};
